<template>
  <div class="content-panel" style="max-width: 1000px;">
    <div class="panel-header">
      <div class="panel-icon">
        <v-icon size="32" color="#1e3a8a">mdi-account-group</v-icon>
      </div>
      <div class="panel-title-section">
        <h2 class="panel-title">사원 정보 등록하기</h2>
      </div>
    </div>

    <div class="panel-body">
      <div class="instruction-card" :class="{ 'move-bottom': hasEmployees }">
        <div class="instruction-icon">
          <v-icon color="#1e3a8a">mdi-monitor-arrow-down</v-icon>
        </div>
        <div class="instruction-content">
          <h4>파일 다운로드 안내</h4>
          <ol>
            <li>장기요양정보시스템 접속 후 우측 상단 <strong>기타</strong> &gt; <strong>엑셀 다운로드</strong>를 클릭합니다.</li>
            <li>원하는 탭(요양보호사 정보/청구내역/청구내역 상세)을 선택하고 <strong>엑셀 다운로드</strong> 버튼을 누릅니다.</li>
            <li>브라우저 저장 창에서 <strong>Ctrl + Shift + S</strong>로 파일을 저장해 주세요.</li>
          </ol>
        </div>
      </div>

      <div v-if="!hasEmployees" class="upload-empty-state">
        <div class="empty-icon">
          <v-icon size="56" color="#94a3b8">mdi-cloud-upload</v-icon>
        </div>
        <div class="empty-text">
          <p>사원 정보 파일을 업로드하면 자동으로 목록이 채워집니다.</p>
          <span>장기요양정보시스템에서 다운로드한 엑셀을 그대로 업로드하세요.</span>
        </div>
        <button class="btn-primary-large" @click="handleUpload" :disabled="isConfirming">
          <v-icon class="btn-icon">mdi-cloud-upload</v-icon>
          <span>사원 정보 파일 업로드하기</span>
        </button>
      </div>

      <div v-if="employees.length > 0" class="employees-table-card">
        <div class="employees-table-header">
          <div>
            <h4>등록된 사원 정보</h4>
            <p class="header-subtitle">업로드된 사원 데이터 전체를 확인하세요</p>
          </div>
          <span class="employees-count">{{ employees.length }}명</span>
        </div>
        <div class="employees-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>성명</th>
                <th>생년월일</th>
                <th>성별</th>
                <th>보유 자격증</th>
                <th>근무기간</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(employee, index) in employees" :key="employee.lem_idx || index">
                <td>
                  <div class="employee-cell">
                    <v-icon size="18" color="#1e3a8a" class="cell-icon">mdi-account</v-icon>
                    <span>{{ employee.name || employee.lem_name || '이름 없음' }}</span>
                    <v-chip 
                      v-if="isBoss(employee)" 
                      color="#10b981" 
                      size="x-small" 
                      variant="flat"
                      class="boss-chip"
                    >
                      대표자
                    </v-chip>
                  </div>
                </td>
                <td>{{ formatBirth(employee.birthDate || employee.lem_birth) }}</td>
                <td>{{ formatGender(employee.gender || employee.lem_gender) }}</td>
                <td>{{ employee.qualification || employee.lem_license || '-' }}</td>
                <td>{{ formatEmploymentPeriodDisplay(employee) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="hasEmployees" class="status-card" :class="isConfirmed ? 'info' : 'success'">
        <v-icon :color="isConfirmed ? '#10b981' : '#00C73C'" size="24">
          {{ isConfirmed ? 'mdi-lock-check' : 'mdi-check-circle' }}
        </v-icon>
        <div class="status-card-text">
          <h4>{{ isConfirmed ? '사원 정보 확정됨' : '사원 정보 업로드 완료' }}</h4>
          <p>
            {{ isConfirmed
              ? '확정 상태예요. 해제 후에만 사원 정보를 수정하거나 재업로드할 수 있습니다.'
              : `${employees.length}명의 사원 정보가 등록되었습니다. 확정을 눌러 다음 단계로 진행하세요.` }}
          </p>
        </div>
        <div class="status-card-actions">
          <button class="btn-outline" @click="handleReupload" :disabled="!canReupload || isConfirming">
            재업로드
          </button>
          <template v-if="isConfirmed">
            <button class="btn-outline" @click="handleRelease" :disabled="isConfirming">
              <v-progress-circular
                v-if="isConfirming"
                indeterminate
                size="16"
                width="2"
                color="#047857"
                class="mr-1"
              />
              확정 해제
            </button>
          </template>
          <template v-else>
            <button class="btn-outline boss-btn" @click="openBossDialog" :disabled="!hasEmployees || isConfirming">
              <v-icon size="16" class="mr-1">mdi-account-star</v-icon>
              대표자 설정하기
            </button>
            <button class="btn-outline confirm-emphasis" @click="handleConfirm" :disabled="!hasEmployees || isConfirming">
              <v-progress-circular
                v-if="isConfirming"
                indeterminate
                size="16"
                width="2"
                color="#047857"
                class="mr-1"
              />
              사원 정보 확정하기
            </button>
          </template>
        </div>
      </div>

      <!-- 확정 확인 다이얼로그 -->
      <v-dialog v-model="showConfirmDialog" max-width="440px">
        <v-card class="confirm-dialog">
          <div class="confirm-dialog-header">
            <v-icon size="48" color="#10b981">mdi-lock-check</v-icon>
            <h3>사원 정보 확정</h3>
          </div>
          <div class="confirm-dialog-body">
            <p>사원 정보를 확정하시겠습니까?</p>
            <p>확정 후에는 사원 정보를 수정할 수 없습니다.</p>
          </div>
          <div class="confirm-dialog-footer">
            <button class="btn-cancel" @click="showConfirmDialog = false">취소</button>
            <button class="btn-confirm" @click="proceedConfirm">확정</button>
          </div>
        </v-card>
      </v-dialog>

      <!-- 해제 확인 다이얼로그 -->
      <v-dialog v-model="showReleaseDialog" max-width="440px">
        <v-card class="confirm-dialog">
          <div class="confirm-dialog-header warning">
            <v-icon size="48" color="#f59e0b">mdi-alert-circle</v-icon>
            <h3>확정 해제</h3>
          </div>
          <div class="confirm-dialog-body">
            <p>사원 정보 확정을 해제하시겠습니까?</p>
            <p>해제 후에는 다시 수정 및 재업로드가 가능합니다.</p>
          </div>
          <div class="confirm-dialog-footer">
            <button class="btn-cancel" @click="showReleaseDialog = false">취소</button>
            <button class="btn-confirm warning" @click="proceedRelease">해제</button>
          </div>
        </v-card>
      </v-dialog>

      <!-- 대표자 설정 다이얼로그 -->
      <v-dialog v-model="showBossDialog" max-width="500px">
        <v-card class="boss-dialog">
          <div class="boss-dialog-header">
            <v-icon size="32" color="#10b981">mdi-account-star</v-icon>
            <h3>대표자 설정</h3>
          </div>
          <div class="boss-dialog-body">
            <p class="boss-dialog-desc">대표자로 지정할 사원을 선택하세요.</p>
            <div class="boss-list">
              <div 
                v-for="(employee, index) in employees" 
                :key="employee.lem_idx || index"
                class="boss-item"
                :class="{ 'selected': isSelectedBoss(employee) }"
                @click="toggleBoss(employee)"
              >
                <v-icon size="20" :color="isSelectedBoss(employee) ? '#10b981' : '#64748b'">
                  {{ isSelectedBoss(employee) ? 'mdi-account-star' : 'mdi-account' }}
                </v-icon>
                <span class="boss-name">{{ employee.name || employee.lem_name }}</span>
                <span class="boss-birth">{{ formatBirth(employee.birthDate || employee.lem_birth) }}</span>
                <v-icon 
                  v-if="isSelectedBoss(employee)" 
                  size="20" 
                  color="#10b981"
                  class="check-icon"
                >
                  mdi-check-circle
                </v-icon>
              </div>
            </div>
          </div>
          <div class="boss-dialog-footer">
            <button class="btn-cancel" @click="showBossDialog = false">취소</button>
            <button class="btn-confirm boss" @click="saveBoss" :disabled="isSavingBoss">
              <v-progress-circular
                v-if="isSavingBoss"
                indeterminate
                size="16"
                width="2"
                color="#fff"
                class="mr-1"
              />
              저장
            </button>
          </div>
        </v-card>
      </v-dialog>

    
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

interface EmployeeRow {
  [key: string]: any;
}

const props = defineProps<{
  employees: EmployeeRow[];
  stepCompleted: boolean;
  isConfirmed?: boolean;
  isConfirming?: boolean;
}>();

const emit = defineEmits<{
  (e: 'upload'): void;
  (e: 'confirm'): void;
  (e: 'release'): void;
  (e: 'boss-updated'): void;
}>();

const store = useStore();

const employees = computed(() => props.employees || []);
const stepCompleted = computed(() => props.stepCompleted);
const isConfirmed = computed(() => !!props.isConfirmed);
const isConfirming = computed(() => !!props.isConfirming);
const hasEmployees = computed(() => employees.value.length > 0);
const canReupload = computed(() => stepCompleted.value && hasEmployees.value && !isConfirmed.value);

// 확정/해제 확인 다이얼로그 상태
const showConfirmDialog = ref(false);
const showReleaseDialog = ref(false);

// 대표자 설정 다이얼로그 상태
const showBossDialog = ref(false);
const selectedBossIdx = ref<number | null>(null);
const isSavingBoss = ref(false);

// 대표자 여부 확인
const isBoss = (employee: EmployeeRow) => {
  return employee.lem_isboss === 'Y' || employee.isBoss === 'Y';
};

// 선택된 대표자 여부 확인
const isSelectedBoss = (employee: EmployeeRow) => {
  const idx = employee.lem_idx || employee.idx;
  return selectedBossIdx.value === idx;
};

// 대표자 설정 다이얼로그 열기
const openBossDialog = () => {
  // 현재 대표자가 있으면 선택 상태로 설정
  const currentBoss = employees.value.find(emp => isBoss(emp));
  if (currentBoss) {
    selectedBossIdx.value = currentBoss.lem_idx || currentBoss.idx;
  } else {
    selectedBossIdx.value = null;
  }
  showBossDialog.value = true;
};

// 대표자 토글
const toggleBoss = (employee: EmployeeRow) => {
  const idx = employee.lem_idx || employee.idx;
  if (selectedBossIdx.value === idx) {
    selectedBossIdx.value = null;
  } else {
    selectedBossIdx.value = idx;
  }
};

// 대표자 저장
const saveBoss = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code) {
    store.commit('showSnackbar', { text: '사용자 정보를 찾을 수 없습니다.', color: 'error' });
    return;
  }

  try {
    isSavingBoss.value = true;
    
    const response = await axios.post('/api/emp/set-boss', {
      sisulCode: user.m_sisul_code,
      bossIdx: selectedBossIdx.value
    }, { withCredentials: true });

    if (response.data.success) {
      store.commit('showSnackbar', { text: '대표자가 설정되었습니다.', color: 'success' });
      showBossDialog.value = false;
      emit('boss-updated');
    } else {
      store.commit('showSnackbar', { text: response.data.message || '대표자 설정에 실패했습니다.', color: 'error' });
    }
  } catch (error: any) {
    console.error('대표자 설정 오류:', error);
    store.commit('showSnackbar', { text: '대표자 설정 중 오류가 발생했습니다.', color: 'error' });
  } finally {
    isSavingBoss.value = false;
  }
};

const handleUpload = () => {
  if (isConfirming.value) return;
  emit('upload');
};

const handleReupload = () => {
  if (!canReupload.value || isConfirming.value) return;
  emit('upload');
};

// 확정 버튼 클릭
const handleConfirm = () => {
  showConfirmDialog.value = true;
};

// 확정 진행
const proceedConfirm = () => {
  showConfirmDialog.value = false;
  emit('confirm');
};

// 해제 버튼 클릭
const handleRelease = () => {
  showReleaseDialog.value = true;
};

// 해제 진행
const proceedRelease = () => {
  showReleaseDialog.value = false;
  emit('release');
};

const formatDate = (value?: string | number | null) => {
  if (!value) return '-';
  const str = value.toString();
  if (str.includes('-')) return str;
  if (str.length === 8) {
    return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
  }
  return str;
};

const formatBirth = (value?: string | number | null) => {
  if (!value) return '-';
  const formatted = formatDate(value);
  return formatted === '-' ? '-' : formatted;
};

const formatGender = (value?: string | number | null) => {
  if (value === undefined || value === null) return '-';
  if (typeof value === 'string') {
    const normalized = value.trim().toUpperCase();
    if (['M', '남', 'MALE', '1'].includes(normalized)) return '남';
    if (['F', '여', 'FEMALE', '2'].includes(normalized)) return '여';
  }
  if (typeof value === 'number') {
    return value === 1 ? '남' : value === 2 ? '여' : '-';
  }
  return '-';
};

const formatEmploymentPeriod = (start?: string | number | null, end?: string | number | null) => {
  const startFormatted = formatDate(start);
  const endFormatted = formatDate(end);
  if (startFormatted === '-' && endFormatted === '-') return '-';
  return `${startFormatted} ~ ${endFormatted === '-' ? '현재' : endFormatted}`;
};

const formatEmploymentPeriodDisplay = (employee: EmployeeRow) => {
  if (!employee) return '-';
  if (employee.workPeriod) return employee.workPeriod;

  const start =
    employee.workStartDate ??
    employee.work_start_date ??
    employee.lem_work_start_date;
  const end =
    employee.workEndDate ??
    employee.work_end_date ??
    employee.lem_work_end_date;

  return formatEmploymentPeriod(start, end);
};
</script>

<style lang="scss">
@import '@/assets/styles/wageProcess.scss';

.confirm-dialog {
  border-radius: 16px !important;
  overflow: hidden;

  .confirm-dialog-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 32px 24px 24px 24px;
    border-bottom: 1px solid #f1f3f5;
    background: #ecfdf5;

    &.warning {
      background: #fef3c7;
    }

    h3 {
      font-size: 1.25rem;
      font-weight: 700;
      color: #191f28;
      margin: 0;
    }
  }

  .confirm-dialog-body {
    padding: 24px 32px;
    text-align: center;

    p {
      font-size: 0.938rem;
      color: #4b5563;
      line-height: 1.6;
      margin: 0 0 8px 0;

      &:last-child {
        margin-bottom: 0;
        font-weight: 600;
        color: #191f28;
      }
    }
  }

  .confirm-dialog-footer {
    display: flex;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid #f1f3f5;
    background: #fafbfc;

    .btn-cancel {
      flex: 1;
      height: 48px;
      background: white;
      color: #4b5563;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      font-size: 0.938rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #f9fafb;
        border-color: #d1d5db;
      }
    }

    .btn-confirm {
      flex: 1;
      height: 48px;
      background: #10b981;
      color: white;
      border: none;
      border-radius: 12px;
      font-size: 0.938rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);

      &:hover {
        background: #059669;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }

      &.warning {
        background: #f59e0b;
        box-shadow: 0 2px 8px rgba(245, 158, 11, 0.25);

        &:hover {
          background: #d97706;
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.35);
        }
      }
    }
  }
}

.status-card-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.confirm-emphasis {
  background: linear-gradient(135deg, #1e3a8a, #3b82f6);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 8px 20px rgba(30, 58, 138, 0.35);

  &:hover {
    background: linear-gradient(135deg, #1b2f6b, #2563eb);
    box-shadow: 0 10px 24px rgba(30, 58, 138, 0.45);
  }

  &:disabled {
    background: #94a3b8;
    box-shadow: none;
  }
}

.upload-empty-state {
  margin-top: 32px;
  padding: 48px 32px;
  border: 1px dashed #d1d5db;
  border-radius: 24px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;

  .empty-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .empty-text {
    p {
      margin: 0;
      font-size: 1.05rem;
      font-weight: 600;
      color: #0f172a;
    }

    span {
      display: block;
      margin-top: 6px;
      color: #475569;
      font-size: 0.93rem;
    }
  }
}

// 대표자 칩
.boss-chip {
  margin-left: 8px;
  font-weight: 600;
}

// 대표자 설정 버튼
.boss-btn {
  background: linear-gradient(135deg, #10b981, #10b981) !important;
  color: #fff !important;
  border-color: transparent !important;

  &:hover {
    background: linear-gradient(135deg, #10b981, #10b981) !important;
  }

  &:disabled {
    background: #94a3b8 !important;
  }
}

// 대표자 설정 다이얼로그
.boss-dialog {
  border-radius: 16px !important;
  overflow: hidden;

  .boss-dialog-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 24px 24px 16px 24px;
    border-bottom: 1px solid #f1f3f5;
    background: linear-gradient(135deg, #fef3c7, #fde68a);

    h3 {
      font-size: 1.25rem;
      font-weight: 700;
      color: #191f28;
      margin: 0;
    }
  }

  .boss-dialog-body {
    padding: 20px 24px;

    .boss-dialog-desc {
      font-size: 0.875rem;
      color: #64748b;
      margin: 0 0 16px 0;
      text-align: center;
    }

    .boss-list {
      max-height: 300px;
      overflow-y: auto;
      border: 1px solid #e5e7eb;
      border-radius: 12px;

      .boss-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        cursor: pointer;
        transition: all 0.15s ease;
        border-bottom: 1px solid #f1f5f9;

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background: #f8fafc;
        }

        &.selected {
          background: #fef3c7;
        }

        .boss-name {
          flex: 1;
          font-weight: 600;
          color: #1e293b;
        }

        .boss-birth {
          font-size: 0.875rem;
          color: #64748b;
        }

        .check-icon {
          margin-left: auto;
        }
      }
    }
  }

  .boss-dialog-footer {
    display: flex;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid #f1f3f5;
    background: #fafbfc;

    .btn-cancel {
      flex: 1;
      height: 44px;
      background: white;
      color: #4b5563;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #f9fafb;
        border-color: #d1d5db;
      }
    }

    .btn-confirm.boss {
      flex: 1;
      height: 44px;
      background: #f59e0b;
      color: white;
      border: none;
      border-radius: 10px;
      font-size: 0.875rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover:not(:disabled) {
        background: #d97706;
      }

      &:disabled {
        background: #94a3b8;
        cursor: not-allowed;
      }
    }
  }
}

</style>